import {
  Avatar,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import errorMessages from "../../stripe/errorMessages";
import React, { useCallback, useEffect, useState } from "react";
import { doc, getFirestore, writeBatch } from "firebase/firestore";
import { processCustomerBundle } from "../products/fetchers/products";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { GUIDES } from "../../constants/routes";
import Image from "next/image";
import LoadingButton from "../../components/LoadingButton";
import MuiDialogTitle from "@mui/material/DialogTitle";
import axios from "axios";
import { firebaseApp } from "../../firebase/firebaseClient";
import getStripe from "../../stripe/getStripe";
import { guideModalStyles } from "../../theme/guideModalStyles";
import logger from "../../utils/logger";
import { useAuth } from "../../firebase/useAuth";
import { useRouter } from "next/router";
import useUser from "../../firebase/useUser";
interface OwnProps {
  openPayModal: boolean;
  productValues: {
    title: string;
    name: string;
    price: number;
    tax: number;
    total_discount_amounts: number;
    price_with_tax: number;
  };
  purchaseSucceded: boolean;
  setPurchaseSucceded: (purchaseSucceded: boolean) => void;
  confirmPayment: any;
  setProductValues: any;
  closePurchaseModal: () => void;
}
type Props = OwnProps;
const ConfirmModal: React.FC<Props> = ({
  openPayModal,
  productValues,
  confirmPayment,
  setProductValues,
  closePurchaseModal,
  setPurchaseSucceded,
  purchaseSucceded,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const stripe = useStripe();
  const router = useRouter();
  const authUser = useAuth();
  const classes = guideModalStyles();
  const firestore = getFirestore(firebaseApp);
  const [message, setMessage] = useState(null);
  const { user } = useUser(authUser?.user?.uid);
  const [couponCode, setCouponCode] = useState("");
  const [disableBtn, setDisableBtn] = useState(false);
  const [couponMsg, setCouponMsg] = useState({
    text: "",
    isValid: false,
  });

  //this handles when the modal is closed after discount is applied
  useEffect(() => {
    setCouponMsg({
      text: "",
      isValid: false,
    });
    setMessage(null);
    setDisableBtn(false);
    return () => {};
  }, [openPayModal]);

  const handleCodeChange = (e) => {
    setCouponCode(e.target.value);
  };

  const AddCouponCode = useCallback(async () => {
    //verify discount code
    const response = await axios.post("/api/verify_code", {
      coupon_code: couponCode,
    });
    if (response.data.status === "success") {
      setDisableBtn(true);
      //apply discount and update total
      const update_Invoice = await axios.post("/api/update_invoice", {
        invoice_id: confirmPayment.invoice_id,
        discount_id: response.data.promotionCodes.coupon.id,
      });

      const finalise_invoice = await axios.post("/api/finalize_invoice", {
        invoice_id: confirmPayment.invoice_id,
      });

      setProductValues({
        name: finalise_invoice.data.description,
        title: finalise_invoice.data.statement_descriptor,
        price: finalise_invoice.data.subtotal / 100,
        tax: finalise_invoice.data.tax / 100,
        price_with_tax: finalise_invoice.data.total / 100,
        total_discount_amounts:
          finalise_invoice.data.total_discount_amounts[0].amount / 100,
      });
      setCouponMsg({
        text: "Discount Applied",
        isValid: true,
      });
    } else {
      setCouponMsg({
        text: response.data.message,
        isValid: false,
      });
    }
  }, [confirmPayment.invoice_id, couponCode, setCouponMsg, setProductValues]);

  const processPayment = async () => {
    if (!stripe) {
      return;
    }
    setIsLoading(true);
    try {
      const pay_invoice = await axios.post("/api/pay_invoice", {
        invoice_id: confirmPayment.invoice_id,
        payment_method: confirmPayment.payment_method,
      });
      if (pay_invoice.status !== 200) {
        setMessage("Something went wrong.");
      } else {
        //write the guide to the user purchased
        const batch = writeBatch(firestore);
        let guideName = productValues.name;
        if (guideName === "standard" || guideName === "premium") {
          const response: any = await processCustomerBundle(
            guideName,
            user.user_id
          );
          if (response.success) {
            setPurchaseSucceded(response.success);
          }
        } else {
          if (guideName === "caregiver" || guideName === "patient") {
            batch.set(
              doc(
                firestore,
                "guide_purchases",
                "users",
                user?.user_id,
                "caregiver"
              ),
              {}
            );
            batch.set(
              doc(
                firestore,
                "guide_purchases",
                "users",
                user?.user_id,
                "patient"
              ),
              {}
            );
          }
          batch.set(
            doc(
              firestore,
              "guide_purchases",
              "users",
              user?.user_id,
              guideName
            ),
            {}
          );
          await batch.commit();
          setPurchaseSucceded(true);
        }
        setMessage("Payment Successful.");
        setIsLoading(false);
        router.query?.id &&
          (await router.push({ pathname: `${GUIDES}/${router.query.id}` }));
        setDisableBtn(false);
      }
    } catch (error) {
      const msg = errorMessages(error.response.data.messageCode);
      logger.e(
        "Log: ~ file: PurchaseModal.tsx:191 ~ processPayment ~ error",
        error
      );
      setMessage(msg);
      setIsLoading(false);
    }
  };
  const DiscountSection: React.FC = () => (
    <>
      <Grid
        container
        wrap="nowrap"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{ padding: "10px 4px" }}
      >
        <Grid item xs={12}>
          <>
            {couponMsg.isValid ? (
              <Typography variant="h6" style={{ color: "green" }}>
                {couponMsg.text}
              </Typography>
            ) : (
              <TextField
                id="coupon_code"
                name="coupon_code"
                label="Enter Coupon Code"
                variant="filled"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <LoadingButton
                        variant="contained"
                        color="secondary"
                        disabled={disableBtn}
                        onClick={AddCouponCode}
                        loading={disableBtn}
                        size="large"
                        style={{
                          borderRadius: "12px",
                          minHeight: 47,
                          marginRight: -11,
                        }}
                      >
                        Apply Discount
                      </LoadingButton>
                    </InputAdornment>
                  ),
                }}
                fullWidth
                value={couponCode}
                onChange={handleCodeChange}
                autoComplete="off"
                autoFocus={true}
              />
            )}
          </>
        </Grid>
      </Grid>
      <Grid container direction="row" justifyContent="center" spacing={1}>
        <Grid item>
          <Typography
            variant="h6"
            style={{ color: !couponMsg.isValid ? "red" : "" }}
          >
            {!couponMsg.isValid ? couponMsg.text : ""}
          </Typography>
          <Typography variant="h6">{message}</Typography>
        </Grid>
      </Grid>
    </>
  );
  const PriceBreakdown: React.FC<{
    label: string;
    amount: number;
    total?: boolean;
  }> = ({ label, amount, total }) => (
    <div>
      <hr style={{ border: "0.5px solid #C4C4C4", margin: 0 }} />
      <Grid
        container
        wrap="nowrap"
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        style={{ padding: "15px 20px" }}
      >
        <Grid item xs={6}>
          <Typography
            variant="body2"
            style={{
              fontFamily: "roboto",
              fontWeight: 400,
              fontSize: 16,
              color: !total && "rgba(0, 0, 0, 0.6)",
            }}
          >
            {label}
          </Typography>
        </Grid>
        <Grid item xs={6} alignItems="flex-end">
          <Typography
            variant={total ? "h6" : "body2"}
            style={{
              textAlign: "right",
              color: !total && "rgba(0, 0, 0, 0.6)",
            }}
          >
            ${amount.toFixed(2)}
          </Typography>
        </Grid>
      </Grid>
    </div>
  );

  return (
    <Elements stripe={getStripe()}>
      <Dialog
        open={openPayModal}
        onClose={closePurchaseModal}
        aria-labelledby="purchase-dialog-title"
        aria-describedby="purchase-dialog-description"
        keepMounted={false}
        fullWidth={true}
      >
        <MuiDialogTitle>
          {purchaseSucceded ? (
            <div className={classes.sucessIcon}>
              <Image
                src="/public-new/success.svg"
                alt="success"
                width={130}
                height={128}
              />
            </div>
          ) : null}
          <div className={classes.sucessIcon}>
            <Typography
              variant="h5"
              style={{
                color: "#393C74",
                fontFamily: "lato",
                fontWeight: 500,
                fontSize: "28px",
                margin: 7,
              }}
            >
              {purchaseSucceded ? message : "Confirm Your Purchase"}
            </Typography>
          </div>
          <div className={classes.sucessIcon}>
            <Typography
              variant="body2"
              style={{
                fontFamily: "roboto",
                fontWeight: 400,
                fontSize: "14px",
                margin: 7,
              }}
            >
              {purchaseSucceded && "Thank you for your purchase."}
            </Typography>
          </div>
          <IconButton
            aria-label="close"
            onClick={closePurchaseModal}
            className={classes.closeButton}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        {purchaseSucceded ? null : (
          <DialogContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper
                  style={{
                    borderRadius: 10,
                    boxShadow: "0px 7px 7px 0px #0000000D",
                  }}
                >
                  <Grid
                    container
                    wrap="nowrap"
                    spacing={6}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Grid item xs={12} md={2}>
                      <Avatar
                        variant="square"
                        src={`/public-new/${productValues.name}-icon.png`}
                        alt="guide-icon"
                        style={{
                          height: 60,
                          width: 60,
                          margin: 10,
                          marginLeft: 20,
                        }}
                      ></Avatar>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        variant="body2"
                        style={{
                          fontFamily: "roboto",
                          fontWeight: 400,
                          fontSize: 16,
                        }}
                      >
                        {productValues.title}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography
                        variant="h6"
                        style={{ textAlign: "right", marginRight: "20px" }}
                      >
                        ${productValues.price}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
                <div style={{ marginTop: "26px" }}>
                  <DiscountSection />
                  <PriceBreakdown
                    label="Sales Tax"
                    amount={productValues?.tax}
                  />
                  <PriceBreakdown
                    label="Guide Savings"
                    amount={productValues?.total_discount_amounts}
                  />
                  <PriceBreakdown
                    label="Order Total"
                    amount={productValues.price_with_tax}
                    total
                  />
                </div>
              </Grid>
            </Grid>
            <Grid container direction="row" justifyContent="center" spacing={1}>
              <Grid item>
                <Typography variant="h6">{message}</Typography>
              </Grid>
            </Grid>
            <Grid
              container
              direction="row"
              justifyContent="center"
              spacing={1}
              style={{ marginTop: 20 }}
            >
              <Grid item>
                <LoadingButton
                  disabled={isLoading || !stripe || purchaseSucceded}
                  onClick={processPayment}
                  variant="contained"
                  color="secondary"
                  fullWidth
                  loading={isLoading}
                  size={"large"}
                >
                  Buy now
                </LoadingButton>
              </Grid>
            </Grid>
          </DialogContent>
        )}
      </Dialog>
    </Elements>
  );
};

export default ConfirmModal;
